import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import useIsMount from '@/util/hook/useIsMount.ts';

interface IProps {
    className?: string;
    latitude?: number;
    longitude?: number;
}

const DefaultMap: React.FunctionComponent<IProps> = ({ className, latitude, longitude }) => {
    const { isMount } = useIsMount();
    const mapRef = useRef<google.maps.Map | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const center = {
        lat: latitude ?? 37.5759,  // 기본값으로 서울 광화문 좌표 설정
        lng: longitude ?? 126.9768
    };

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.panTo(center);
        }
    }, [latitude, longitude]);

    // 지도가 로드되면 애니메이션 효과를 위해 상태 변경
    useEffect(() => {
        if (isMount && latitude && longitude) {
            setTimeout(() => setIsLoaded(true), 500);
        }
    }, [isMount, latitude, longitude]);

    const mapOptions = {
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,         // 줌 컨트롤 활성화
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
            {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{visibility: 'off'}]  // POI 라벨 제거
            },
            {
                featureType: 'water',
                elementType: 'geometry',
                stylers: [{ color: '#e9e9e9' }]
            },
            {
                featureType: 'landscape',
                elementType: 'geometry',
                stylers: [{ color: '#f5f5f5' }]
            }
        ]
    };

    return (
        <div 
            className={`w-full h-full ${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500 rounded-lg overflow-hidden shadow-xl`}
            onClick={e => e.stopPropagation()}
        >
            {isMount && latitude && longitude && (
                <GoogleMap
                    mapContainerStyle={{ 
                        width: '100%', 
                        height: '100%',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                    center={center}
                    zoom={mapOptions.zoom}
                    options={mapOptions}
                    onLoad={(map) => {
                        mapRef.current = map;
                        
                        // 맵 로드 후 부드러운 줌 효과
                        const startZoom = 12;
                        map.setZoom(startZoom);
                        
                        const zoomStep = (mapOptions.zoom - startZoom) / 10;
                        let currentZoom = startZoom;
                        
                        const zoomInterval = setInterval(() => {
                            currentZoom += zoomStep;
                            if (currentZoom >= mapOptions.zoom) {
                                clearInterval(zoomInterval);
                                map.setZoom(mapOptions.zoom);
                            } else {
                                map.setZoom(currentZoom);
                            }
                        }, 50);
                    }}
                >
                    {/* MarkerF 컴포넌트를 사용하여 deprecated 경고 해결 */}
                    <MarkerF
                        position={center}
                        animation={google.maps.Animation.DROP}
                        icon={{
                            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                            scaledSize: new google.maps.Size(40, 40),
                            origin: new google.maps.Point(0, 0),
                            anchor: new google.maps.Point(20, 40)
                        }}
                    />
                </GoogleMap>
            )}
        </div>
    );
};

export default DefaultMap;
